/**
 * Otomasyon yetki kontrol utility fonksiyonları
 */

import {
    AUTOMATION_PERMISSION_SCOPES,
    AUTOMATION_PERMISSION_DEPENDENCIES,
    getAllAutomationScopes,
} from "./AutomationPermissionScopes";

/**
 * Kullanıcının belirli bir otomasyonda belirli bir yetkiye sahip olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @param {String} scope - Kontrol edilecek yetki scope'u
 * @returns {Boolean} - Yetkisi var mı yok mu
 */
export function hasAutomationPermission(automation, user, scope) {
    if (!automation || !user) {
        console.log("hasAutomationPermission " + automation?.name , "automation or user is null");
        return false;
    }
    // permissionConfig yoksa veya public modda ise herkes erişebilir
    var permissionConfig = automation.permissionConfig || { mode: "public" };
    // isOwner her zaman full yetkiye sahip
    if (user.isOwner) {
        console.log("hasAutomationPermission " + automation?.name , "user is owner");
        return true;
    }

    // Custom modda yetki kontrolü yap
    if (permissionConfig.mode === "custom") {
        var userScopes = getUserAutomationScopes(automation, user);
        console.log("hasAutomationPermission " + automation?.name , "custom mode, user scopes", userScopes);
        return userScopes.includes(scope);
    }

    // Root kullanıcılar her zaman full yetkiye sahip
    if (user.role && user.role.isRoot) {
        console.log("hasAutomationPermission " + automation?.name , "root user");
        return true;
    }

    if (!permissionConfig || permissionConfig.mode === "public") {
        console.log("hasAutomationPermission " + automation?.name , "public mode");
        return true;
    }
    console.log("hasAutomationPermission " + automation?.name , "no permission");

    return false;
}

/**
 * Kullanıcının bir otomasyon için tüm yetkilerini döndürür
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Array} - Yetki scope'ları listesi
 */
export function getUserAutomationScopes(automation, user) {
    if (!automation || !user) {
        return [];
    }

    // isOwner her zaman full yetkiye sahip
    if (user.isOwner) {
        return getAllAutomationScopes();
    }

    // permissionConfig yoksa veya public modda ise herkes full erişime sahip
    var permissionConfig = automation.permissionConfig || { mode: "public" };
    if (!permissionConfig || permissionConfig.mode === "public") {
        return getAllAutomationScopes();
    }

    // Custom modda kullanıcının yetkilerini bul
    if (permissionConfig.mode === "custom" && Array.isArray(permissionConfig.custom)) {
        var scopes = [];
        var userId = user._id ? user._id.toString() : user.toString();
        var userDepartmentIds = [];

        // Kullanıcının departmanlarını al
        if (user.subjects && Array.isArray(user.subjects)) {
            userDepartmentIds = user.subjects.map(function (s) {
                return s._id ? s._id.toString() : s.toString();
            });
        }

        // Her bir custom config'i kontrol et
        permissionConfig.custom.forEach(function (config) {
            var isMatch = false;

            if (config.target === "user" && config.user) {
                var configUserId = config.user._id ? config.user._id.toString() : config.user.toString();
                isMatch = configUserId === userId;
            } else if (config.target === "department" && config.department) {
                var configDeptId = config.department._id
                    ? config.department._id.toString()
                    : config.department.toString();
                isMatch = userDepartmentIds.includes(configDeptId);
            }

            if (isMatch && Array.isArray(config.scopes)) {
                scopes = scopes.concat(config.scopes);
            }
        });

        // Tekrarları kaldır
        return scopes.filter(function (scope, index, self) {
            return self.indexOf(scope) === index;
        });
    }

    // Root kullanıcılar her zaman full yetkiye sahip
    if (user.role && user.role.isRoot) {
        return getAllAutomationScopes();
    }

    return [];
}

/**
 * Bir scope'un tüm bağımlılıklarını recursive olarak döndürür
 * @param {String} scope - Scope
 * @returns {Array} - Bağımlılıklar listesi
 */
export function getAutomationScopeDependencies(scope) {
    var dependencies = AUTOMATION_PERMISSION_DEPENDENCIES[scope] || [];
    var allDependencies = [];

    dependencies.forEach(function (dep) {
        allDependencies.push(dep);
        var subDeps = getAutomationScopeDependencies(dep);
        allDependencies = allDependencies.concat(subDeps);
    });

    // Tekrarları kaldır
    return allDependencies.filter(function (dep, index, self) {
        return self.indexOf(dep) === index;
    });
}

/**
 * Bir scope kaldırıldığında hangi scope'ların da kaldırılması gerektiğini döndürür
 * @param {String} scope - Kaldırılacak scope
 * @returns {Array} - Kaldırılması gereken bağımlı scope'lar
 */
export function findDependentAutomationScopes(scope) {
    var dependents = [];

    Object.keys(AUTOMATION_PERMISSION_DEPENDENCIES).forEach(function (s) {
        if (AUTOMATION_PERMISSION_DEPENDENCIES[s].includes(scope)) {
            dependents.push(s);
            // Recursive olarak bu scope'a bağlı olanları da bul
            var subDependents = findDependentAutomationScopes(s);
            dependents = dependents.concat(subDependents);
        }
    });

    // Tekrarları kaldır
    return dependents.filter(function (dep, index, self) {
        return self.indexOf(dep) === index;
    });
}

/**
 * Kullanıcının otomasyonu görebilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canViewAutomation(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_READ);
}

/**
 * Kullanıcının otomasyon flow'unu güncelleyebilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canUpdateAutomationFlow(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_FLOW_UPDATE);
}

/**
 * Kullanıcının otomasyon meta bilgilerini güncelleyebilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canUpdateAutomationMeta(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_META_UPDATE);
}

/**
 * Kullanıcının otomasyonu yayınlayabilir/yayından kaldırabilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canPublishAutomation(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_PUBLISH);
}

/**
 * Kullanıcının otomasyonu silebilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canDeleteAutomation(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_DELETE);
}

/**
 * Kullanıcının otomasyonu kopyalayabilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canDuplicateAutomation(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_DUPLICATE);
}

/**
 * Kullanıcının otomasyon planlarını görebilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canViewAutomationPlan(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_PLAN_VIEW);
}

/**
 * Kullanıcının otomasyon planlarını iptal edebilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canCancelAutomationPlan(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_PLAN_CANCEL);
}

/**
 * Kullanıcının otomasyonu yeniden planlayabilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canRescheduleAutomation(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_RESCHEDULE);
}

/**
 * Kullanıcının otomasyon prefilter'ını güncelleyebilir olup olmadığını kontrol eder
 * @param {Object} automation - Otomasyon objesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canUpdateAutomationPrefilter(automation, user) {
    return hasAutomationPermission(automation, user, AUTOMATION_PERMISSION_SCOPES.AUTOMATION_PREFILTER_UPDATE);
}

/**
 * Kullanıcının otomasyon yetkilerini düzenleyebilir olup olmadığını kontrol eder
 * Sadece root kullanıcılar düzenleyebilir
 * @param {Object} user - Kullanıcı objesi
 * @returns {Boolean}
 */
export function canEditAutomationPermissions(user) {
    if (!user) {
        return false;
    }
    return user.role && user.role.isRoot;
}

/**
 * Hedef kullanıcının yetkilerinin düzenlenip düzenlenemeyeceğini kontrol eder
 * isOwner olan kullanıcıların yetkileri düzenlenemez
 * @param {Object} targetUser - Hedef kullanıcı objesi
 * @returns {Boolean}
 */
export function canEditUserPermissions(targetUser) {
    if (!targetUser) {
        return true;
    }
    // isOwner olan kullanıcıların yetkileri düzenlenemez
    return !targetUser.isOwner;
}

/**
 * Otomasyonları kullanıcı yetkisine göre filtreler (read yetkisi olmayanları çıkarır)
 * @param {Array} automations - Otomasyon listesi
 * @param {Object} user - Kullanıcı objesi
 * @returns {Array} - Filtrelenmiş otomasyon listesi
 */
export function filterAutomationsByPermission(automations, user) {
    if (!Array.isArray(automations) || !user) {
        return [];
    }

    return automations.filter(function (automation) {
        return canViewAutomation(automation, user);
    });
}
